import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { ChatDepartment } from '@nucleus-commerce/api-client/admin';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import { useChatAgents } from '@/features/conversations';

import { useDepartmentMutations } from '../hooks/use-department-mutations';
import { useDepartments } from '../hooks/use-departments';

import { ConfirmDialog } from './confirm-dialog';
import { DepartmentDialog } from './department-dialog';

interface EditorState {
  department: ChatDepartment | null;
}

/**
 * Teams a chat can be routed to. With at least one, the pre-chat form can offer a department
 * field; the default department takes conversations where the shopper chose none.
 */
export function DepartmentsTab({ storeId }: { storeId: string }) {
  const { t } = useTranslation('chatSettings');
  const { can } = usePermissions();
  const canManage = can('conversations.manage');
  const query = useDepartments(storeId);
  const agents = useChatAgents(storeId);
  const { remove } = useDepartmentMutations(storeId);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [deleting, setDeleting] = useState<ChatDepartment | null>(null);

  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  if (!query.data) return <Skeleton className="h-40 max-w-3xl" aria-busy="true" />;

  const departments = [...query.data].sort((a, b) => a.position - b.position);
  const names = new Map(agents.data?.map((agent) => [agent.userId, agent.name]));
  const nextPosition =
    departments.length === 0 ? 0 : Math.max(...departments.map((d) => d.position)) + 1;

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      <FormSection title={t('departments.title')} description={t('departments.description')}>
        {departments.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('departments.empty')}</p>
        ) : (
          <ul className="divide-y rounded-md border" aria-label={t('departments.title')}>
            {departments.map((department) => {
              const members = department.memberIds.map((id) => names.get(id)).filter(Boolean);
              return (
                <li key={department.id} className="flex items-start gap-3 p-3">
                  <div className="min-w-0 flex-1 space-y-1">
                    <p className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium">{department.name}</span>
                      {department.isDefault ? (
                        <Badge variant="secondary">{t('departments.default')}</Badge>
                      ) : null}
                    </p>
                    {department.description ? (
                      <p className="text-muted-foreground text-sm">{department.description}</p>
                    ) : null}
                    <p className="text-muted-foreground text-xs">
                      {members.length === department.memberIds.length && members.length > 0
                        ? members.join(', ')
                        : t('departments.memberCount', { count: department.memberIds.length })}
                    </p>
                  </div>
                  {canManage ? (
                    <div className="flex shrink-0 gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={t('departments.edit', { name: department.name })}
                        onClick={() => setEditor({ department })}
                      >
                        <Pencil aria-hidden />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={t('departments.delete', { name: department.name })}
                        onClick={() => setDeleting(department)}
                      >
                        <Trash2 aria-hidden />
                      </Button>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
        {canManage ? (
          <div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setEditor({ department: null })}
            >
              <Plus aria-hidden />
              {t('departments.add')}
            </Button>
          </div>
        ) : null}
      </FormSection>
      {editor ? (
        <DepartmentDialog
          key={editor.department?.id ?? 'new'}
          storeId={storeId}
          department={editor.department}
          nextPosition={nextPosition}
          agents={agents.data ?? null}
          onOpenChange={(open) => {
            if (!open) setEditor(null);
          }}
        />
      ) : null}
      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
        title={t('departments.deleteTitle', { name: deleting?.name ?? '' })}
        description={t('departments.deleteDescription')}
        confirmLabel={t('departments.deleteConfirm')}
        pending={remove.isPending}
        onConfirm={() => {
          if (deleting) remove.mutate(deleting.id, { onSettled: () => setDeleting(null) });
        }}
      />
    </div>
  );
}
